import React, { ChangeEvent, FormEvent, MouseEvent } from 'react';
import Bugsnag from '@bugsnag/js';
import { getParameterByName } from 'utils/utils';
import { withPageStaticProps } from 'utils/header';
import auth from 'utils/auth';
import { resetPassword, loginV3 } from 'services/accountsApi';
import Message from 'components/Message';
import FormInput from 'components/FormInput';
import DoubleHeader from 'components/DoubleHeader';
import { Customer } from 'types';
import Link from 'next/link';

interface State {
  password: string;
  passwordError: string | boolean;
  submitting: boolean;
  submitError: boolean;
  isAuthorizingError: boolean;
}

class ResetPassword extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      password: '',
      passwordError: '',
      submitting: false,
      submitError: false,
      isAuthorizingError: false,
    };
  }

  handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (this.state.password === '') {
      this.setState({ passwordError: true });
      return;
    }

    this.setState({
      submitting: true,
      passwordError: false,
      submitError: false,
      isAuthorizingError: false,
    });

    try {
      const data = (await resetPassword(this.state.password, getParameterByName('auth_token')!)) as Customer;

      const loginData = (await loginV3(data.email!, this.state.password)) as Customer;

      auth.signIn(loginData);

      window.location.href = '/my-account?activeAccountTab=my-events';
    } catch (e: any) {
      Bugsnag.notify(e);

      this.setState({
        submitError: true,
        isAuthorizingError: e?.status === 401,
        submitting: false,
      });
    }
  };

  render() {
    return (
      <div className="m-section container">
        <div className="row">
          <div className="col-span-12 space-y-32 xs:col-span-8 xs:col-start-3 md:col-span-6 md:col-start-4 lg:col-span-4 lg:col-start-5">
            <DoubleHeader smallTitle={<h1>Reset Password</h1>} largeTitle={<h2>So many passwords, so little time.</h2>} />

            <div>
              <FormInput
                darkMode
                className="w-full"
                id="password"
                type="password"
                label="Enter a new password"
                placeholder="New Password"
                value={this.state.password}
                errorMessage={this.state.passwordError ? 'Please provide a new password.' : ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  this.setState({
                    password: e.target!.value,
                  })
                }
              />
            </div>

            <button
              className="btn-info btn mb-16"
              type="submit"
              onClick={(e: MouseEvent) => this.handleSubmit(e)}
              disabled={this.state.submitting}
            >
              Submit
            </button>

            {this.state.submitError && (
              <Message type="error">
                {this.state.isAuthorizingError ? (
                  <>
                    Your reset password email has expired. Request a new password reset email on the{' '}
                    <a className="font-bold" href={`${process.env.NEXT_PUBLIC_APP_URL}/account/login`}>
                      login screen
                    </a>
                    .
                  </>
                ) : (
                  <>
                    There was an error resetting your password. Please try again, and if the error persists, contact{' '}
                    <Link href="/support" className="font-bold">
                      customer service
                    </Link>{' '}
                    for assistance.
                  </>
                )}
              </Message>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export const getStaticProps = withPageStaticProps();

export default ResetPassword;
